import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

export default function TextEditor() {
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [texts, setTexts] = useState<{ id: string; value: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!user) return;
        if (!isAdmin) {
            navigate("/");
            return;
        }
        const fetchTexts = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "appTexts"));
                const items = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    value: doc.data().value || "",
                }));
                setTexts(items);
            } catch (err) {
                console.error(err);
                setError("Fehler beim Laden der Texte.");
            } finally {
                setLoading(false);
            }
        };
        fetchTexts();
    }, [user, isAdmin, navigate]);

    const handleChange = (id: string, newValue: string) => {
        setTexts((prev) =>
            prev.map((item) => (item.id === id ? { ...item, value: newValue } : item))
        );
    };

    const saveText = async (id: string, value: string) => {
        setSaving(id);
        setError("");
        try {
            await updateDoc(doc(db, "appTexts", id), { value });
        } catch (err) {
            console.error(err);
            setError(`Fehler beim Speichern von "${id}"`);
        } finally {
            setSaving(null);
        }
    };

    const addNewText = async () => {
        const key = prompt("Schlüssel (z.B. home_new_feature):");
        if (!key) return;
        const value = prompt("Standardtext (kann später geändert werden):") || "";
        try {
            await setDoc(doc(db, "appTexts", key), { value });
            const snapshot = await getDocs(collection(db, "appTexts"));
            const items = snapshot.docs.map((d) => ({ id: d.id, value: d.data().value }));
            setTexts(items);
            alert(`✅ Textbaustein "${key}" wurde angelegt.`);
        } catch (err) {
            console.error(err);
            alert("❌ Fehler beim Anlegen. Bist du als Admin eingeloggt?");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-green-600 text-xl">Lade Textbausteine…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center mb-2">
                    <h1 className="text-3xl font-bold text-gray-800">Text-Editor</h1>
                    <button
                        onClick={addNewText}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                    >
                        + Neuen Textbaustein
                    </button>
                </div>
                <p className="text-gray-600 mb-8">
                    Bearbeite hier alle dynamischen Texte der Webseite. Änderungen werden sofort übernommen.
                </p>
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                        {error}
                    </div>
                )}
                <div className="space-y-6">
                    {texts.map(({ id, value }) => (
                        <div
                            key={id}
                            className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition"
                        >
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {id}
                                <span className="ml-2 text-xs text-gray-400 font-normal">
                                    (Schlüssel)
                                </span>
                            </label>
                            <textarea
                                value={value}
                                onChange={(e) => handleChange(id, e.target.value)}
                                rows={3}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                            <div className="flex justify-end mt-3">
                                <button
                                    onClick={() => saveText(id, value)}
                                    disabled={saving === id}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                                >
                                    {saving === id ? "Speichert…" : "Speichern"}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-10 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    💡 <strong>Tipp:</strong> Klicke auf <strong>"+ Neuen Textbaustein"</strong>, um eigene Schlüssel zu erstellen.
                </div>
            </div>
        </div>
    );
}